import { SectionCard, WorkspaceField } from '@components/Common/EntityWorkspace';
import SelectFormField from '@components/Common/SelectFormField';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import type { HrUser } from '@components/Common/Utils/useHrUsers';
import { jobApplicationPlanSave } from '@constants/graphQLQueries';
import dayjs from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import React, { useState } from 'react';
import { refusalText } from './StatusCard';
import { formatDate } from '@helpers/date';

interface Draft {
  ownerId: string;
  nextAction: string;
  nextActionDate: string;
}

/**
 * Who is responsible for this candidate, and what happens next.
 *
 * The owner defaults to the job's recruiter; only a Super Admin moves a candidate to someone
 * else. The next action and its date feed the follow-ups due list and the morning reminder, so a
 * candidate cannot go quiet without somebody's name on it.
 */
const NextActionCard: React.FC<{
  applicationId: string;
  ownerId?: string | null;
  nextAction?: string | null;
  nextActionAt?: string | null;
  hrUsers: HrUser[];
  canEdit: boolean;
  canChangeOwner: boolean;
  onChanged: () => Promise<void> | void;
  onError: (message: string) => void;
}> = ({ applicationId, ownerId, nextAction, nextActionAt, hrUsers, canEdit, canChangeOwner, onChanged, onError }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({ ownerId: '', nextAction: '', nextActionDate: '' });

  const ownerName = ownerId ? (hrUsers.find((u) => u._id === ownerId)?.name ?? ownerId) : '';
  const due = nextActionAt ? dayjs(nextActionAt) : null;
  const overdue = !!due && due.isBefore(dayjs(), 'day');

  const startEdit = () => {
    setDraft({
      ownerId: ownerId ?? '',
      nextAction: nextAction ?? '',
      nextActionDate: due ? due.format('YYYY-MM-DD') : '',
    });
    setEditing(true);
  };

  const save = async () => {
    try {
      await fetchGraphQLApiData(
        jobApplicationPlanSave(applicationId, {
          ownerId: canChangeOwner && draft.ownerId !== (ownerId ?? '') ? draft.ownerId : undefined,
          nextAction: draft.nextAction.trim(),
          nextActionDate: draft.nextAction.trim() ? draft.nextActionDate : '',
        }),
        { showStatus: true, message: 'Saved' },
      );
      setEditing(false);
      await onChanged();
    } catch (e) {
      onError(refusalText(e, 'Could not save the next action. Please try again.'));
    }
  };

  const set = (k: keyof Draft, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <SectionCard
      title="Owner and next action"
      icon={<AssignmentIndOutlinedIcon />}
      meta={overdue ? 'Overdue' : undefined}
      editing={editing}
      onEdit={canEdit ? startEdit : undefined}
      onCancel={() => setEditing(false)}
      onSave={save}
    >
      {editing ? (
        <>
          <WorkspaceField
            label="Recruiter"
            editing={canChangeOwner}
            value={ownerName || 'Not assigned'}
            edit={(
              <SelectFormField
                required={false}
                name="ownerId"
                label=""
                value={draft.ownerId}
                options={[{ value: '', label: 'Not assigned' }, ...hrUsers.map((u) => ({ value: u._id, label: u.name }))]}
                onChange={(_n, v) => set('ownerId', String(v))}
                selectProps={{ inputProps: { 'aria-label': 'Recruiter' } }}
              />
            )}
          />
          <WorkspaceField
            label="Due"
            editing
            edit={<WorkspaceInput type="date" value={draft.nextActionDate} onChange={(v) => set('nextActionDate', v)} />}
          />
          <WorkspaceField
            label="Next action"
            full
            editing
            edit={(
              <WorkspaceInput
                value={draft.nextAction}
                onChange={(v) => set('nextAction', v)}
                placeholder="For example: call to confirm the interview slot"
              />
            )}
          />
        </>
      ) : (
        <>
          <WorkspaceField label="Recruiter" value={ownerName || 'Not assigned'} />
          <WorkspaceField label="Due" value={formatDate(due, '')} mono />
          <WorkspaceField label="Next action" full value={nextAction ?? ''} />
        </>
      )}
    </SectionCard>
  );
};

export default NextActionCard;
